// Mail — inbound emails, sent log, manual compose & reply (admin only)
import { api } from '../api';
import { esc, $, $$, setHTML, timeAgo, icons } from '../helpers';
import { toast, openModal, closeModal, setBreadcrumb, skeletonTable } from '../ui';

const FROM_ADDRESSES = [
  'noreply@gravantlabs.com',
  'finance@gravantlabs.com',
  'hello@gravantlabs.com',
  'info@gravantlabs.com',
  'contact@gravantlabs.com',
  'legal@gravantlabs.com',
  'dmca@gravantlabs.com',
];

let activeTab: 'inbox' | 'sent' = 'inbox';

// ─── Page entry ───────────────────────────────────────────────────────────────
export async function renderInbox() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Mail' }]);
  setHTML(content, skeletonTable(5, 4));
  try {
    const tableHtml = activeTab === 'inbox' ? await renderInboundTable() : await renderSentTable();
    const tab = (id: string, label: string) =>
      '<button class="btn ' + (activeTab === id ? 'btn-primary' : 'btn-secondary') + '" data-tab="' + id + '">' + label + '</button>';
    setHTML(content, '<div class="page-enter"><div class="page-header"><h1>Mail</h1>'
      + '<div class="page-header-actions"><button class="btn btn-secondary" id="refresh-btn"><i data-lucide="refresh-cw"></i> Refresh</button><button class="btn btn-primary" id="compose-btn"><i data-lucide="send"></i> Compose</button></div>'
      + '</div>'
      + '<div style="display:flex;gap:0.5rem;margin-bottom:1rem">' + tab('inbox', 'Inbox') + tab('sent', 'Sent') + '</div>'
      + '<div class="card"><div class="card-body no-pad">' + tableHtml + '</div></div></div>');
    icons();
    $$('[data-tab]').forEach(b => b.addEventListener('click', () => {
      activeTab = b.getAttribute('data-tab') as 'inbox' | 'sent';
      renderInbox();
    }));
    document.getElementById('refresh-btn')?.addEventListener('click', () => renderInbox());
    document.getElementById('compose-btn')?.addEventListener('click', () => openComposeModal());
    if (activeTab === 'inbox') {
      $$('[data-email-id]').forEach(row => row.addEventListener('click', () => openEmail(parseInt(row.getAttribute('data-email-id')!))));
    } else {
      $$('[data-sent-id]').forEach(row => row.addEventListener('click', () => openSent(parseInt(row.getAttribute('data-sent-id')!))));
    }
  } catch (err: any) { toast(err.message, 'error'); }
}

async function renderInboundTable(): Promise<string> {
  const data = await api.get('/admin/inbox');
  const emails = data.emails || [];
  if (!emails.length) return '<div class="empty-state"><p>No emails yet</p></div>';
  let rows = '';
  emails.forEach((m: any) => {
    const unread = !m.is_read;
    rows += '<tr style="cursor:pointer' + (unread ? ';font-weight:600' : '') + '" data-email-id="' + m.id + '">'
      + '<td>' + (unread ? '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:var(--accent);margin-right:0.45rem;vertical-align:middle"></span>' : '') + esc(m.from_addr || '—') + '</td>'
      + '<td>' + esc(m.subject || '(no subject)') + '</td>'
      + '<td class="text-muted">' + esc(m.to_addr || '') + '</td>'
      + '<td>' + timeAgo(m.received_at) + '</td>'
      + '</tr>';
  });
  return '<div class="table-wrapper"><table><thead><tr><th>From</th><th>Subject</th><th>To</th><th>Received</th></tr></thead><tbody>' + rows + '</tbody></table></div>';
}

async function renderSentTable(): Promise<string> {
  const data = await api.get('/admin/sent');
  const emails = data.emails || [];
  if (!emails.length) return '<div class="empty-state"><p>No sent emails yet</p></div>';
  let rows = '';
  emails.forEach((m: any) => {
    const failed = m.status === 'failed';
    rows += '<tr style="cursor:pointer" data-sent-id="' + m.id + '">'
      + '<td>' + esc(m.to_addr || '—') + '</td>'
      + '<td>' + esc(m.subject || '(no subject)') + '</td>'
      + '<td class="text-muted">' + esc(m.from_addr || '') + '</td>'
      + '<td>' + (failed ? '<span style="color:var(--danger,#c0392b)">failed</span>' : '<span class="text-muted">sent</span>') + '</td>'
      + '<td>' + timeAgo(m.sent_at) + '</td>'
      + '</tr>';
  });
  return '<div class="table-wrapper"><table><thead><tr><th>To</th><th>Subject</th><th>From</th><th>Status</th><th>Sent</th></tr></thead><tbody>' + rows + '</tbody></table></div>';
}

// ─── Read an inbound email ───────────────────────────────────────────────────
async function openEmail(id: number) {
  try {
    const data = await api.get('/admin/inbox/' + id);
    const m = data.email;
    const hasText = !!(m.text_body && String(m.text_body).trim());
    let bodyHtml: string;
    if (hasText) {
      bodyHtml = '<div style="white-space:pre-wrap;line-height:1.6;max-height:55vh;overflow:auto">' + esc(m.text_body) + '</div>';
    } else if (m.html_body) {
      // Placeholder only — the untrusted HTML is mounted via DOM srcdoc property below (never string-built into an attribute).
      bodyHtml = '<div id="email-html-mount" style="width:100%;height:55vh;border:1px solid var(--border);border-radius:8px;background:#fff;overflow:hidden"></div>';
    } else {
      bodyHtml = '<p class="text-muted">(empty message)</p>';
    }
    openModal(esc(m.subject || '(no subject)'),
      '<div style="margin-bottom:0.8rem;font-size:0.85rem" class="text-muted">'
      + '<div><strong>From:</strong> ' + esc(m.from_addr || '') + '</div>'
      + '<div><strong>To:</strong> ' + esc(m.to_addr || '') + '</div>'
      + '<div><strong>Received:</strong> ' + esc(m.received_at || '') + '</div>'
      + '</div>' + bodyHtml
      + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="email-close">Close</button><button type="button" class="btn btn-primary" id="email-reply"><i data-lucide="reply"></i> Reply</button></div>');
    // Mount untrusted email HTML safely: srcdoc set via DOM property (no attribute escaping), sandbox='' blocks scripts & same-origin.
    if (!hasText && m.html_body) {
      const mount = document.getElementById('email-html-mount');
      if (mount) {
        const f = document.createElement('iframe');
        f.setAttribute('sandbox', '');
        f.style.cssText = 'width:100%;height:100%;border:0;background:#fff';
        f.srcdoc = String(m.html_body);
        mount.appendChild(f);
      }
    }
    document.getElementById('email-close')?.addEventListener('click', () => { closeModal(); renderInbox(); });
    document.getElementById('email-reply')?.addEventListener('click', () => {
      const subj = String(m.subject || '');
      openComposeModal({ to: m.from_addr || '', subject: subj.startsWith('Re:') ? subj : 'Re: ' + subj });
    });
    icons();
  } catch (err: any) { toast(err.message, 'error'); }
}

// ─── Read a sent email ───────────────────────────────────────────────────────
async function openSent(id: number) {
  try {
    const data = await api.get('/admin/sent/' + id);
    const m = data.email;
    const bodyHtml = m.html_body
      ? '<div id="sent-html-mount" style="width:100%;height:55vh;border:1px solid var(--border);border-radius:8px;background:#fff;overflow:hidden"></div>'
      : '<p class="text-muted">(empty message)</p>';
    openModal(esc(m.subject || '(no subject)'),
      '<div style="margin-bottom:0.8rem;font-size:0.85rem" class="text-muted">'
      + '<div><strong>From:</strong> ' + esc(m.from_addr || '') + '</div>'
      + '<div><strong>To:</strong> ' + esc(m.to_addr || '') + '</div>'
      + '<div><strong>Status:</strong> ' + esc(m.status || '') + '</div>'
      + '<div><strong>Sent:</strong> ' + esc(m.sent_at || '') + '</div>'
      + '</div>' + bodyHtml
      + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="sent-close">Close</button></div>');
    // Our own template HTML, but mount the same XSS-safe way for consistency.
    if (m.html_body) {
      const mount = document.getElementById('sent-html-mount');
      if (mount) {
        const f = document.createElement('iframe');
        f.setAttribute('sandbox', '');
        f.style.cssText = 'width:100%;height:100%;border:0;background:#fff';
        f.srcdoc = String(m.html_body);
        mount.appendChild(f);
      }
    }
    document.getElementById('sent-close')?.addEventListener('click', closeModal);
    icons();
  } catch (err: any) { toast(err.message, 'error'); }
}

// ─── Compose / Reply & send ──────────────────────────────────────────────────
function openComposeModal(prefill?: { to?: string; subject?: string }) {
  const fromOpts = FROM_ADDRESSES.map(a => '<option value="' + a + '">' + a + '</option>').join('');
  openModal(prefill ? 'Reply' : 'Compose Email', '<form id="compose-form">'
    + '<div class="form-group"><label>From *</label><select name="from" required>' + fromOpts + '</select></div>'
    + '<div class="form-group"><label>To *</label><input type="email" name="to" required placeholder="recipient@example.com"></div>'
    + '<div class="form-group"><label>Subject *</label><input type="text" name="subject" required maxlength="200" placeholder="Subject"></div>'
    + '<div class="form-group"><label>Message *</label><textarea name="body" rows="8" required maxlength="10000" placeholder="Write your message..."></textarea></div>'
    + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="compose-cancel">Cancel</button><button type="submit" class="btn btn-primary"><i data-lucide="send"></i> Send</button></div></form>');
  // Prefill via DOM property — values may come from untrusted email headers, never via HTML attributes.
  if (prefill) {
    const toEl = document.querySelector('#compose-form [name="to"]') as HTMLInputElement | null;
    const subjEl = document.querySelector('#compose-form [name="subject"]') as HTMLInputElement | null;
    if (toEl && prefill.to) toEl.value = prefill.to;
    if (subjEl && prefill.subject) subjEl.value = prefill.subject;
  }
  document.getElementById('compose-cancel')?.addEventListener('click', closeModal);
  icons();
  document.getElementById('compose-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const body = Object.fromEntries(fd);
    const btn = (e.target as HTMLFormElement).querySelector('button[type="submit"]') as HTMLButtonElement;
    if (btn) btn.disabled = true;
    try {
      await api.post('/admin/send-email', body);
      toast('Email sent', 'success');
      closeModal();
      if (activeTab === 'sent') renderInbox();
    } catch (err: any) {
      toast(err.message, 'error');
    } finally {
      if (btn) btn.disabled = false;
    }
  });
}
